import { useMemo, useState } from 'react'
import {
  Controller,
  useController,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useDebounce } from 'use-debounce'
import to from 'await-to-js'
import { toast } from 'react-toastify'
import { handleFormError } from 'utils/handleFormError'
import {
  AutoGrowTextarea,
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  Field,
  FieldHint,
  FieldLabel,
  Input,
  MultiCombobox,
  PhoneInput,
  Radio,
  RadioGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@firstbite/ui'
import useApiService from 'services/api'
import { formatInteger } from 'utils/formatting'
import { ContactCompany } from 'models/companies'
import {
  ContactResponse,
  DepartmentLabels,
  SeniorityLabels,
} from '../../../models/contacts'
import { CONTACT_TITLES } from 'utils/constants'
import { ContactDefaultValues } from './ContactModal'

/**
 * DS2026 → @firstbite/ui rebuild of the Add/Edit Contact details form. Same
 * react-hook-form field names + zod schema as the legacy `ContactDetailsStep`;
 * only the presentation moves onto the fb-ui package primitives (Field/Input/
 * Textarea/Select/Combobox/MultiCombobox/PhoneInput), so the whole modal can run
 * inside the react-aria `Dialog`. Data still lives in the app: queries here,
 * validation/submit in `ContactModal`.
 */

const errText = (msg?: unknown): string | undefined =>
  typeof msg === 'string' ? msg : undefined

// This modal marks the *required* fields (the minority) rather than tagging
// every optional one — required is the exception here, so it's what should carry
// a marker. Muted treatment, matching fb-ui's own "(optional)" suffix (no red
// asterisk, per DS2026).
const RequiredMark = () => (
  <span className="ml-1 font-normal text-fbui-gray-500">(required)</span>
)

/** Async, searchable company picker + a "Create new company" row. */
function CompanyCombobox({
  disabled,
  initialCompany,
}: {
  disabled?: boolean
  initialCompany?: ContactCompany
}) {
  const api = useApiService()
  const { control, setValue } = useFormContext<ContactDefaultValues>()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced] = useDebounce(query, 400)

  const { data, isFetching } = useQuery({
    queryKey: ['companies_list_search', debounced],
    queryFn: async ({ signal }) =>
      api.searchCompaniesList({ search: debounced }, signal),
    enabled: debounced.trim() !== '' && !disabled,
  })

  const results = useMemo(
    () =>
      (data ?? []).map((c) => ({
        id: String(c.contact_company_id),
        name: c.name,
        domain: c.domain,
        meta: [
          c.is_unmatched != null
            ? c.is_unmatched
              ? 'Custom'
              : 'First Bite'
            : undefined,
          c.sub_category_name,
          c.location_count && c.location_count > 1
            ? `${formatInteger(c.location_count)} locations`
            : [c.city, c.state].filter(Boolean).join(', '),
        ]
          .filter(Boolean)
          .join(' · '),
        company: {
          id: c.contact_company_id,
          domain: c.domain,
          name: c.name,
          category: c.category,
          sub_category: c.sub_category,
          // Carry the location count from the search result so the scope radio
          // can appear immediately on selection (no wait on the door-count fetch).
          location_count: c.location_count ?? null,
        } as ContactDefaultValues['contact_company'],
      })),
    [data]
  )

  const trimmed = query.trim()
  const hasExact = results.some(
    (r) => r.name.toLowerCase() === trimmed.toLowerCase()
  )
  const canCreate = trimmed.length > 0 && !isFetching && !hasExact

  return (
    <Controller
      control={control}
      name="contact_company"
      render={({ field, fieldState }) => {
        const selected = field.value as
          | ContactDefaultValues['contact_company']
          | undefined
        const selectedName = selected?.name ?? initialCompany?.name ?? ''
        const selectedId =
          selected?.id != null ? String(selected.id) : undefined

        // Changing the company re-scopes the contact: reset to "entire company"
        // and clear any door selection so a stale scope can't carry over.
        const resetLocationScope = () => {
          setValue('apply_to', 'entire_company')
          setValue('door_ids', [])
        }
        const applyExisting = (r: (typeof results)[number]) => {
          field.onChange(r.company)
          setValue('company_domain', r.domain ?? '', { shouldValidate: true })
          setValue('category_id', undefined)
          setValue('sub_category_id', undefined)
          resetLocationScope()
        }
        const applyNew = () => {
          field.onChange({
            id: -1,
            name: trimmed,
            domain: undefined,
          } as ContactDefaultValues['contact_company'])
          setValue('company_domain', '')
          resetLocationScope()
        }

        return (
          <Field
            status={fieldState.error ? 'error' : 'default'}
            className="w-full"
          >
            <FieldLabel>
              Company name
              <RequiredMark />
            </FieldLabel>
            <Combobox
              aria-label="Company name"
              disabled={disabled}
              invalid={Boolean(fieldState.error)}
              defaultFilter={() => true}
              allowsEmptyCollection
              selectedKey={selectedId ?? null}
              inputValue={open ? query : selectedName}
              onInputChange={(v) => {
                setQuery(v)
                if (!open) setOpen(true)
              }}
              onOpenChange={(isOpen) => {
                setOpen(isOpen)
                if (!isOpen) setQuery('')
              }}
              onSelectionChange={(key) => {
                if (key === '__create__') applyNew()
                else {
                  const r = results.find((x) => x.id === key)
                  if (r) applyExisting(r)
                }
                setOpen(false)
                setQuery('')
              }}
            >
              <ComboboxInput
                placeholder="Start typing company name"
                selectOnFocus
              />
              <ComboboxContent
                loading={isFetching}
                emptyMessage={query ? 'No companies found' : 'Start typing…'}
              >
                {canCreate ? (
                  <ComboboxItem
                    key="__create__"
                    id="__create__"
                    textValue={trimmed}
                  >
                    Create “{trimmed}”
                  </ComboboxItem>
                ) : null}
                {results.map((r) => (
                  <ComboboxItem key={r.id} id={r.id} textValue={r.name}>
                    <span className="flex flex-col">
                      <span>
                        <span className="font-medium">{r.name}</span>
                        {r.domain ? ` — ${r.domain}` : ''}
                      </span>
                      {r.meta ? (
                        <span className="text-xs text-fbui-gray-500">
                          {r.meta}
                        </span>
                      ) : null}
                    </span>
                  </ComboboxItem>
                ))}
              </ComboboxContent>
            </Combobox>
            <FieldHint>{errText(fieldState.error?.message)}</FieldHint>
          </Field>
        )
      }}
    />
  )
}

/**
 * Step-1 scope picker (shown only for an existing multi-location company): apply
 * the contact to the entire company, or to specific locations. Choosing "Select
 * locations" adds a second step (the location table); the hint sets that up.
 */
function ApplyToRadioField() {
  const { control } = useFormContext<ContactDefaultValues>()
  const { field } = useController({ name: 'apply_to', control })
  const applyTo = (field.value as string) ?? 'entire_company'
  return (
    <div className="flex flex-col gap-2">
      <FieldLabel>Apply this contact to</FieldLabel>
      <RadioGroup
        value={applyTo}
        onValueChange={field.onChange}
        className="flex flex-col gap-3 sm:flex-row sm:gap-6"
      >
        <Radio value="entire_company" label="Entire company" />
        <Radio value="select_locations" label="Select locations" />
      </RadioGroup>
      {applyTo === 'select_locations' && (
        // text-sm to match the "Contacts match to companies by domain" caption,
        // not the smaller text-xs field hints.
        <p className="text-sm text-fbui-gray-500">
          You’ll choose which locations in the next step.
        </p>
      )}
    </div>
  )
}

/** Phone number + extension (parity with the legacy modal's phone row). */
function PhoneField() {
  const { control } = useFormContext<ContactDefaultValues>()
  const phone = useController({ control, name: 'phone_number' })
  const ext = useController({ control, name: 'extension' })
  return (
    <Field
      status={phone.fieldState.error ? 'error' : 'default'}
      className="w-full"
    >
      <FieldLabel>Phone number</FieldLabel>
      <PhoneInput
        value={(phone.field.value as string) ?? undefined}
        onChange={(v) => phone.field.onChange(v ?? null)}
        onBlur={phone.field.onBlur}
        withExtension
        extension={(ext.field.value as string) ?? ''}
        onExtensionChange={(v) => ext.field.onChange(v)}
      />
      <FieldHint>{errText(phone.fieldState.error?.message)}</FieldHint>
    </Field>
  )
}

/**
 * Title — choose a common title or create a custom one. Full parity with the
 * legacy CreatableSelect: an explicit "Create …" row commits the typed text
 * (creation happens on select, not on blur), mirroring the company picker.
 */
function TitleField({ options }: { options: string[] }) {
  const { control } = useFormContext<ContactDefaultValues>()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  return (
    <Controller
      control={control}
      name="title"
      render={({ field }) => {
        const selected = (field.value as string) ?? ''
        const q = query.trim()
        const filtered = q
          ? options.filter((o) => o.toLowerCase().includes(q.toLowerCase()))
          : options
        const hasExact = options.some(
          (o) => o.toLowerCase() === q.toLowerCase()
        )
        const canCreate = q.length > 0 && !hasExact

        return (
          <Field className="w-full min-w-0">
            <FieldLabel>Title</FieldLabel>
            <Combobox
              aria-label="Title"
              defaultFilter={() => true}
              allowsEmptyCollection
              selectedKey={selected || null}
              inputValue={open ? query : selected}
              onInputChange={(v) => {
                setQuery(v)
                if (!open) setOpen(true)
              }}
              onOpenChange={(isOpen) => {
                setOpen(isOpen)
                if (!isOpen) setQuery('')
              }}
              onSelectionChange={(key) => {
                if (key === '__create__') field.onChange(q)
                else if (key != null) field.onChange(String(key))
                setOpen(false)
                setQuery('')
              }}
            >
              <ComboboxInput placeholder="Head Chef" selectOnFocus />
              <ComboboxContent
                emptyMessage={query ? 'No matches' : 'Type to add a title'}
              >
                {canCreate ? (
                  <ComboboxItem key="__create__" id="__create__" textValue={q}>
                    Create “{q}”
                  </ComboboxItem>
                ) : null}
                {filtered.map((t) => (
                  <ComboboxItem key={t} id={t}>
                    {t}
                  </ComboboxItem>
                ))}
              </ComboboxContent>
            </Combobox>
          </Field>
        )
      }}
    />
  )
}

/**
 * Labels — multi-select of existing tags, with creation (parity with the legacy
 * CreatableTagSelector). The form stores tag IDs (number[]); the MultiCombobox
 * works in strings. Typing a new label surfaces an "Add …" row that persists the
 * tag via `postTag`, then stores its new id. Freshly-created tags are kept in
 * local `created` state so their chip shows the name immediately (before the
 * tags query refetches with the new row).
 */
function TagsField() {
  const api = useApiService()
  const queryClient = useQueryClient()
  const { control, setError } = useFormContext<ContactDefaultValues>()
  const { data: tagsOptions } = api.useGetTagsOptions(false, 'contacts')
  const [created, setCreated] = useState<{ value: string; label: string }[]>([])

  const options = useMemo(() => {
    const map = new Map<string, { value: string; label: string }>()
    // created first so the canonical (refetched) options overwrite duplicates.
    for (const t of created) map.set(t.value, t)
    for (const t of (tagsOptions ?? []) as {
      label: string
      value: string | number
    }[]) {
      map.set(String(t.value), { value: String(t.value), label: t.label })
    }
    return [...map.values()]
  }, [tagsOptions, created])

  return (
    <Controller
      control={control}
      name="tags"
      render={({ field }) => {
        const known = new Set(options.map((o) => o.value))

        const createTag = async (name: string): Promise<number | null> => {
          if (name.length > 50) {
            toast.error('Tag length limited to 50 characters.')
            return null
          }
          const [err, res] = await to(api.postTag({ tag: name }, 'contacts'))
          if (err || !res?.id) {
            if (err) handleFormError(err, setError)
            return null
          }
          setCreated((prev) => [
            ...prev,
            { value: String(res.id), label: res.tag },
          ])
          void queryClient.invalidateQueries({ queryKey: ['tags', 'contacts'] })
          return res.id
        }

        // A value not in `known` is freshly-typed text (the "Add …" row) — create
        // the tag and swap the raw text for its new id before committing.
        const handleChange = async (next: string[]) => {
          const ids: number[] = []
          for (const v of next) {
            if (known.has(v)) ids.push(Number(v))
            else {
              const id = await createTag(v)
              if (id != null) ids.push(id)
            }
          }
          field.onChange(ids)
        }

        return (
          // min-w-0 lets this flex column shrink below its content so the
          // MultiCombobox collapses overflow chips into the "+N" badge instead of
          // growing the row wider than its 50% share.
          <Field className="w-full min-w-0">
            <FieldLabel>Labels</FieldLabel>
            <MultiCombobox
              aria-label="Labels"
              options={options}
              value={(field.value ?? []).map(String)}
              onChange={(next) => void handleChange(next)}
              allowsCustomValue
              placeholder="VIP, Decision maker"
            />
          </Field>
        )
      }}
    />
  )
}

function TextField({
  name,
  label,
  placeholder,
  required,
  disabled,
}: {
  name: keyof ContactDefaultValues
  label: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
}) {
  const { control } = useFormContext<ContactDefaultValues>()
  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field, fieldState }) => (
        <Field
          status={fieldState.error ? 'error' : 'default'}
          className="w-full"
        >
          <FieldLabel>
            {label}
            {required && <RequiredMark />}
          </FieldLabel>
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            placeholder={placeholder}
            disabled={disabled}
          />
          <FieldHint>{errText(fieldState.error?.message)}</FieldHint>
        </Field>
      )}
    />
  )
}

/** Single-select for a numeric enum field (seniority / department). */
function EnumSelectField({
  name,
  label,
  options,
  placeholder,
}: {
  name: keyof ContactDefaultValues
  label: string
  options: { value: number; label: string }[]
  placeholder?: string
}) {
  const { control } = useFormContext<ContactDefaultValues>()
  return (
    <Controller
      control={control}
      name={name as never}
      render={({ field, fieldState }) => (
        <Field
          status={fieldState.error ? 'error' : 'default'}
          className="w-full min-w-0"
        >
          <FieldLabel>{label}</FieldLabel>
          <Select
            aria-label={label}
            className="w-full"
            placeholder={placeholder}
            invalid={Boolean(fieldState.error)}
            selectedKey={field.value != null ? String(field.value) : null}
            onSelectionChange={(key) =>
              field.onChange(key != null ? Number(key) : null)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.value} id={String(o.value)}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldHint>{errText(fieldState.error?.message)}</FieldHint>
        </Field>
      )}
    />
  )
}

export default function ContactDetailsStepFbui({
  forCompany,
  contactToEdit,
  companyTotalDoors = 0,
}: {
  forCompany?: Partial<ContactCompany>
  contactToEdit?: ContactResponse
  /** Location count for the selected company — the scope radio only shows for an
   *  existing company with more than one location. */
  companyTotalDoors?: number
}) {
  const api = useApiService()
  const { control, setValue } = useFormContext<ContactDefaultValues>()
  const { data: categories } = api.useGetCompanyCategories()

  const contactCompany = useWatch({ control, name: 'contact_company' })
  const categoryId = useWatch({ control, name: 'category_id' })
  const isNewCompany = contactCompany?.id === -1
  // Scope radio only makes sense for an existing company with >1 location
  // (nothing to pick for a new or single-location company). Prefer the count
  // that came with the search result (instant); fall back to the fetched count
  // (covers the edit flow, where the initial company carries no location_count).
  const companyLocationCount = contactCompany?.location_count ?? 0
  const showApplyToRadio =
    !!contactCompany?.id &&
    contactCompany.id > 0 &&
    (companyLocationCount > 1 || companyTotalDoors > 1)

  const initialCompany = (forCompany ?? contactToEdit?.contact_company) as
    | ContactCompany
    | undefined

  const categoryOptions = useMemo(
    () => (categories ?? []).map((c) => ({ id: c.id, name: c.name })),
    [categories]
  )
  const typeOptions = useMemo(() => {
    const cat = (categories ?? []).find((c) => c.id === categoryId)
    return (cat?.sub_categories ?? []).map((s) => ({ id: s.id, name: s.name }))
  }, [categories, categoryId])

  const titleOptions = useMemo(() => {
    const base = [...CONTACT_TITLES]
    const t = contactToEdit?.title
    if (t && !base.includes(t)) base.push(t)
    return base
  }, [contactToEdit?.title])

  const seniorityOptions = useMemo(
    () =>
      Object.entries(SeniorityLabels).map(([value, label]) => ({
        value: Number(value),
        label,
      })),
    []
  )
  const departmentOptions = useMemo(
    () =>
      Object.entries(DepartmentLabels).map(([value, label]) => ({
        value: Number(value),
        label,
      })),
    []
  )

  return (
    // Spacing hierarchy: fields group into clusters (identity · company · contact
    // methods · online · classification · notes). Rows inside a cluster sit tight
    // (gap-3); clusters are separated by more air (gap-5) so the eye reads
    // sections without needing dividers or subheads.
    <div className="flex flex-col gap-5">
      {/* Identity */}
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
        <TextField
          name="first_name"
          label="First name"
          placeholder="Jane"
          required
        />
        <TextField
          name="last_name"
          label="Last name"
          placeholder="Doe"
          required
        />
      </div>

      {/* Company */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
          <CompanyCombobox
            disabled={!!forCompany}
            initialCompany={initialCompany}
          />
          <TextField
            name="company_domain"
            label="Company domain"
            placeholder="company.com"
            disabled={!!forCompany}
          />
        </div>
        {/* Group-level note for the Company + Domain pair (not the modal header).
            text-sm (a step up from the text-xs field-error hints) so it reads as
            a note about both fields, not a hint for one — and so it never gets
            mistaken for / collides with a field's error message, which keeps its
            own slot under each input. No rule lines; cluster spacing separates it. */}
        <p className="pt-0.5 text-sm text-fbui-gray-500">
          Contacts match to companies by domain.{' '}
          <Link
            to="/terms-of-use"
            target="_blank"
            className="font-medium text-fbui-basil-700 transition-colors hover:text-fbui-basil-800"
          >
            Terms of Use
          </Link>
        </p>
        {isNewCompany && (
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
            <Controller
              control={control}
              name="category_id"
              render={({ field, fieldState }) => (
                <Field
                  status={fieldState.error ? 'error' : 'default'}
                  className="w-full"
                >
                  <FieldLabel>
                    Company category
                    <RequiredMark />
                  </FieldLabel>
                  <Select
                    aria-label="Company category"
                    className="w-full"
                    invalid={Boolean(fieldState.error)}
                    selectedKey={
                      field.value != null ? String(field.value) : null
                    }
                    onSelectionChange={(key) => {
                      field.onChange(key != null ? Number(key) : undefined)
                      setValue('sub_category_id', undefined)
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map((c) => (
                        <SelectItem key={c.id} id={String(c.id)}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldHint>{errText(fieldState.error?.message)}</FieldHint>
                </Field>
              )}
            />
            <Controller
              control={control}
              name="sub_category_id"
              render={({ field, fieldState }) => (
                <Field
                  status={fieldState.error ? 'error' : 'default'}
                  className="w-full"
                >
                  <FieldLabel>
                    Company type
                    <RequiredMark />
                  </FieldLabel>
                  <Select
                    aria-label="Company type"
                    className="w-full"
                    disabled={!categoryId}
                    invalid={Boolean(fieldState.error)}
                    selectedKey={
                      field.value != null ? String(field.value) : null
                    }
                    onSelectionChange={(key) =>
                      field.onChange(key != null ? Number(key) : undefined)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {typeOptions.map((s) => (
                        <SelectItem key={s.id} id={String(s.id)}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldHint>{errText(fieldState.error?.message)}</FieldHint>
                </Field>
              )}
            />
          </div>
        )}
        {showApplyToRadio && <ApplyToRadioField />}
      </div>

      {/* Contact methods */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row">
          <TextField
            name="email_work"
            label="Work email"
            placeholder="work@company.com"
          />
          <TextField
            name="email_personal"
            label="Personal email"
            placeholder="personal@example.com"
          />
          <TextField
            name="email_generic"
            label="Other email"
            placeholder="info@company.com"
          />
        </div>
        <PhoneField />
        {/* Group requirement for the contact-methods cluster — it's a one-of
            rule (phone OR any email), not a per-field one, so it lives as a note
            for the whole section rather than a "(required)" tag on each input. */}
        <p className="pt-0.5 text-sm text-fbui-gray-500">
          A phone number or at least one email is required.
        </p>
      </div>

      {/* Online */}
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
        <TextField
          name="url"
          label="Website"
          placeholder="https://www.example.com/"
        />
        <TextField
          name="linkedin_url"
          label="LinkedIn"
          placeholder="https://www.linkedin.com/"
        />
      </div>

      {/* Classification */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
          <TitleField options={titleOptions} />
          <TagsField />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
          <EnumSelectField
            name="seniority"
            label="Seniority"
            options={seniorityOptions}
            placeholder="Select seniority"
          />
          <EnumSelectField
            name="department"
            label="Department"
            options={departmentOptions}
            placeholder="Select department"
          />
        </div>
      </div>

      {/* Notes — add flow only. On edit, notes are managed on the contact
          detail page (multiple, rich-text); a single plain field there would
          clobber them, so it's hidden and excluded from the edit payload. */}
      {!contactToEdit && (
        <Controller
          control={control}
          name="notes"
          render={({ field }) => (
            <Field className="w-full">
              <FieldLabel>Notes</FieldLabel>
              <AutoGrowTextarea
                {...field}
                value={(field.value as string) ?? ''}
                placeholder="Add a note…"
                rows={3}
              />
            </Field>
          )}
        />
      )}
    </div>
  )
}
